import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, HourglassSimple, ArrowRight, X, Rocket } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Onboarding-checklista — visas på Dashboard varje inloggning tills alla
 * sju steg är gröna. Först då blir "×"-knappen aktiverad och kunden kan
 * stänga av widgeten permanent (dismiss).
 *
 * Progress räknas live från domändata så användaren kan inte "fuska"
 * genom att stänga av under tiden.
 */
export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/onboarding/status")
      .then((r) => alive && setStatus(r.data))
      .catch(() => alive && setStatus(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const handleDismiss = async () => {
    if (!status?.is_complete) return;
    setDismissing(true);
    try {
      const r = await api.post("/onboarding/dismiss");
      setStatus(r.data);
      toast.success("Snyggt jobbat! Checklistan är nu dold.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte stänga checklistan");
    } finally {
      setDismissing(false);
    }
  };

  if (loading || !status) return null;
  if (status.dismissed) return null;

  const steps = [
    {
      key: "account",
      done: status.has_account,
      title: "Företagskonto skapat",
      hint: "Ditt konto är aktivt och redo att användas.",
      cta: null,
      to: null,
    },
    {
      key: "company_details",
      done: status.has_company_details,
      title: "Företagsuppgifter ifyllda",
      hint: "Företagsnamnet visas på PDF-rapporter och e-postutskick.",
      cta: "Öppna",
      to: "/settings",
    },
    {
      key: "logo",
      done: status.has_logo,
      title: "Lägg till din logga",
      hint: "Syns på tidrapport-PDF och löneunderlag.",
      cta: "Ladda",
      to: "/settings",
    },
    {
      key: "object",
      done: status.has_object,
      title: "Skapa ditt första objekt",
      hint: "Där dina anställda ska stämpla tid.",
      cta: "Skapa",
      to: "/objects",
    },
    {
      key: "moments",
      done: status.has_two_moments,
      title: "Skapa 2 moment",
      hint: "T.ex. Kaplista, Montering — det anställda stämplar på.",
      cta: "Skapa",
      to: "/objects",
    },
    {
      key: "teammate",
      done: status.has_teammate_or_invite,
      title: "Bjud in första anställd",
      hint: "Skicka magisk länk — de behöver inget lösenord.",
      cta: "Bjud",
      to: "/employees",
    },
    {
      key: "report",
      done: status.has_report,
      title: "Testa stämpelklockan",
      hint: "Gör en probestämpling så du ser hur det funkar.",
      cta: "Testa",
      to: "/",
    },
  ];

  const remaining = status.total_steps - status.completed_steps;
  const canDismiss = status.is_complete;

  const subtitle = status.is_complete
    ? "Allt klart — du kan stänga av checklistan nu."
    : `Du är på god väg — ${remaining} ${remaining === 1 ? "sak" : "saker"} kvar för att komma igång`;

  return (
    <section
      data-testid="onboarding-checklist"
      className="tk-card p-6 mb-6 border border-brand/30 bg-gradient-to-br from-surface to-surface/70"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <div className="flex items-center gap-2">
              <Rocket size={22} weight="fill" className="text-brand flex-shrink-0" />
              <h2 className="font-heading text-2xl text-text-primary leading-tight">
                Kom igång med Tidsrapporten
              </h2>
            </div>
            <span
              data-testid="onboarding-progress-badge"
              className="text-xs font-medium px-2 py-1 rounded-full bg-brand/15 text-brand whitespace-nowrap"
            >
              {status.completed_steps} av {status.total_steps}
            </span>
          </div>
          <p className="text-sm text-text-muted" data-testid="onboarding-subtitle">
            {subtitle}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={!canDismiss || dismissing}
          data-testid="onboarding-dismiss-button"
          className={`flex-shrink-0 p-1 -m-1 rounded transition-colors ${
            canDismiss
              ? "text-text-muted hover:text-text-primary cursor-pointer"
              : "text-text-muted/30 cursor-not-allowed"
          }`}
          title={canDismiss ? "Stäng av checklistan" : "Kan stängas av när alla steg är klara"}
          aria-label={canDismiss ? "Stäng av checklistan" : "Kan stängas av när alla steg är klara"}
        >
          <X size={20} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="mt-5 h-1.5 bg-app rounded-full overflow-hidden">
        <div
          data-testid="onboarding-progress-bar"
          className="h-full bg-brand transition-all duration-500"
          style={{ width: `${status.progress_percent}%` }}
        />
      </div>

      {/* Steps */}
      <ul className="mt-6 space-y-3">
        {steps.map((step) => (
          <li
            key={step.key}
            data-testid={`onboarding-step-${step.key}`}
            data-done={step.done ? "true" : "false"}
            className="flex items-center gap-4 py-2"
          >
            <span className="flex-shrink-0">
              {step.done ? (
                <CheckCircle size={26} weight="fill" className="text-brand" />
              ) : (
                <HourglassSimple size={26} className="text-text-muted/60" />
              )}
            </span>
            <div className="flex-1 min-w-0">
              <div
                className={`text-sm font-medium ${
                  step.done ? "text-text-muted line-through" : "text-text-primary"
                }`}
              >
                {step.title}
              </div>
              {!step.done && (
                <div className="text-xs text-text-muted mt-0.5">{step.hint}</div>
              )}
            </div>
            {!step.done && step.cta && step.to && (
              <button
                type="button"
                onClick={() => navigate(step.to)}
                data-testid={`onboarding-step-${step.key}-cta`}
                className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-brand hover:text-brand-hover transition-colors"
              >
                {step.cta}
                <ArrowRight size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
